import { GlTransactionHeaderResponse, getTransaction, updateReceiptPrintStatus } from "../../services/transactionService";
import { fetchGuarantorsForPrint } from "../../services/loanDynamicFieldsService";
import { formatCurrency } from "../../utils/currencyUtils";
import { Text, Box, Stack, Heading, Columns, Column, Table, TableHeader, TableRow, TableHeaderCell, TableBody, TableCell, TableFooter } from "braid-design-system";
import React from "react";
import Loading from "../Loading";
import { useToast } from "../../hooks/useToast";
import { defaultPrintConfig } from "../../config/defaultPrintConfig";
import { generatePrintHTML } from "../../utils/printRenderer";
import { useTranslation } from "react-i18next";
import { numberToWords } from "../../utils/numberToWords";
import { openPrintWindowHTML } from "../../utils/printUtils";

interface TransactionPrintPayloadAccount {
    account_number?: string;
    account_name?: string;
    amount?: number;
    balance?: number;
}

interface TransactionPrintPayload {
    description?: string;
    transaction_type?: string;
    transaction_number?: string;
    institute_name?: string;
    branch_name?: string;
    branch_address?: string;
    customer_number?: string;
    customer_name?: string;
    total_amount?: number;
    cheque_num?: string;
    cheque_number?: string;
    accounts?: TransactionPrintPayloadAccount[];
    institute_reg_num?: string;
    institute_reg_date?: string;
    entered_by_id?: number | string;
    branch_phone?: string;
    branch_email?: string;
    institute_email?: string;
    institute_phone?: string;
    institute_address_ln1?: string;
    institute_address_ln2?: string;
    institute_address_ln3?: string;
    interest_rate?: string | number;
    period?: string | number;
    interest_rate_period?: string;
    guarantor_members?: string;
}


type GlTransactionHeaderWithPrintPayload = GlTransactionHeaderResponse & {
    print_payload?: TransactionPrintPayload;
};

interface TransactionPrintRow {
    key: string;
    accountNumber: string;
    accountName: string;
    amount: number;
    balance: number;
}

interface TransactionPrintDetailsProps {
    transactionId: number;
    onlyCredits?: boolean;
    onlyDebits?: boolean;
    doPrint?: boolean;
    onPrintReady?: (fn: () => void) => void;
    lang?: string;
    onSubSectionsLoaded?: (subSections: string[]) => void;
    selectedSubSectionProp?: string;
    onSubSectionChangeProp?: (subSection: string) => void;
    onLanguageSelected?: (lang: string) => void;
    onInvoiceInfoLoaded?: (info: { isInvoice: boolean; invoiceHeaderId: number | null }) => void;
}

const TransactionPrintDetails: React.FC<TransactionPrintDetailsProps> = ({
    transactionId,
    onlyCredits = false,
    onlyDebits = false,
    doPrint = false,
    onPrintReady,
    lang,
    onSubSectionsLoaded,
    selectedSubSectionProp,
    onSubSectionChangeProp,
    onLanguageSelected,
    onInvoiceInfoLoaded,
}: TransactionPrintDetailsProps) => {
    const [loading, setLoading] = React.useState(true);
    const [loadingMessage, setLoadingMessage] = React.useState("Please wait, fetching transaction details");
    const [transactionData, setTransactionData] = React.useState<GlTransactionHeaderWithPrintPayload
        | null>(null);

    const [subSections, setSubSections] = React.useState<string[]>([]);
    const [selectedSubSection, setSelectedSubSection] = React.useState("");
    const [fullLoadedConfig, setFullLoadedConfig] = React.useState<any>(null);
    const [guarantorMembersStr, setGuarantorMembersStr] = React.useState("");

    const activeSubSection = selectedSubSectionProp !== undefined ? selectedSubSectionProp : selectedSubSection;

    const handleSubSectionChange = (name: string) => {
        if (onSubSectionChangeProp) {
            onSubSectionChangeProp(name);
        } else {
            setSelectedSubSection(name);
        }
    };

    const resolvedPrintConfig = React.useMemo(() => {
        const baseConfig = transactionData?.print_config as any;
        if (!baseConfig) return null;
        if (activeSubSection && fullLoadedConfig?.sub_sections) {
            const foundObj = fullLoadedConfig.sub_sections.find((obj: any) => Object.keys(obj)[0] === activeSubSection);
            if (foundObj) {
                return foundObj[activeSubSection];
            }
        }
        if (fullLoadedConfig?.sub_sections) {
            const defaultConfig = { ...baseConfig };
            delete defaultConfig.sub_sections;
            return defaultConfig;
        }
        return baseConfig;
    }, [transactionData, activeSubSection, fullLoadedConfig]);

    const containerRef = React.useRef<HTMLDivElement>(null);
    const contentRef = React.useRef<HTMLDivElement>(null);
    const [scale, setScale] = React.useState(1.0);
    const [scaledHeight, setScaledHeight] = React.useState<number | undefined>(undefined);

    const printConfig = (resolvedPrintConfig || defaultPrintConfig) as any;
    const cleanNA = React.useCallback((val: any) => {
        if (printConfig?.hide_na && String(val).trim().toUpperCase() === 'N/A') {
            return "";
        }
        return val;
    }, [printConfig]);
    const cfgWidth = printConfig?.page?.width || "21cm";
    let targetWidthVal = 794;
    if (cfgWidth.endsWith("mm")) {
        targetWidthVal = parseFloat(cfgWidth) * 3.7795275591;
    } else if (cfgWidth.endsWith("cm")) {
        targetWidthVal = parseFloat(cfgWidth) * 37.795275591;
    } else if (cfgWidth.endsWith("px")) {
        targetWidthVal = parseFloat(cfgWidth);
    }

    React.useEffect(() => {
        if (!containerRef.current || !contentRef.current) return;
        
        const updateScale = () => {
            const containerWidth = containerRef.current?.clientWidth || 0;
            const contentEl = contentRef.current;
            if (!contentEl) return;

            const prevTransform = contentEl.style.transform;
            contentEl.style.transform = "none";
            const unscaledWidth = contentEl.offsetWidth || targetWidthVal;
            const unscaledHeight = contentEl.offsetHeight || 1123;
            contentEl.style.transform = prevTransform;

            if (containerWidth > 0 && unscaledWidth > 0) {
                const padding = 16;
                const newScale = Math.min(1.0, (containerWidth - padding) / unscaledWidth);
                setScale(newScale);
                setScaledHeight(unscaledHeight * newScale);
            }
        };

        const timer = setTimeout(updateScale, 100);
        window.addEventListener("resize", updateScale);
        return () => {
            clearTimeout(timer);
            window.removeEventListener("resize", updateScale);
        };
    }, [transactionData, loading]);

    const toast = useToast();
    const { i18n } = useTranslation();
    const activeLang = lang || i18n.language || 'en';
    const isPrintingRef = React.useRef(false);
    const prevDoPrintRef = React.useRef(false);
    const lastPrintedTransactionId = React.useRef<number | null>(null);

    const activeLangRef = React.useRef(activeLang);
    activeLangRef.current = activeLang;

    React.useEffect(() => {
        if (!transactionId) return;
        setLoadingMessage("Please wait, fetching transaction details");
        setLoading(true);
        getTransaction(transactionId).then(async (data) => {
            setTransactionData(data as GlTransactionHeaderWithPrintPayload);
            onInvoiceInfoLoaded?.({
                isInvoice: Boolean(data?.is_invoice),
                invoiceHeaderId: data?.invoice_header_id ?? null,
            });
            const primaryPlTx = data?.pl_transactions?.[0];
            const primaryPlAccount = primaryPlTx?.pl_account;
            const plAccountId = (primaryPlTx as any)?.pl_account_id || primaryPlAccount?.id;
            if (plAccountId) {
                try {
                    const guarantors = await fetchGuarantorsForPrint(plAccountId);
                    if (guarantors && guarantors.length > 0) {
                        const currentLang = activeLangRef.current;
                        const cleanLang = currentLang.startsWith("si") ? "si" : currentLang.startsWith("ta") ? "ta" : "en";
                        const GurLabel = cleanLang === "si" ? "ඇප" : cleanLang === "ta" ? "உத்தரவாதம்" : "Gur";
                        const str = guarantors.map(g => `${GurLabel} ${g.index} - ${g.customer_number}`).join(", ");
                        setGuarantorMembersStr(str);
                    } else {
                        setGuarantorMembersStr("");
                    }
                } catch {
                    setGuarantorMembersStr("");
                }
            } else {
                setGuarantorMembersStr("");
            }
        }).catch((error) => {
            toast.showError(`Error fetching transaction: ${error.message}`);
        }).finally(() => {
            setLoading(false);
        });
    }, [transactionId, doPrint]);

    // Extract sub-section info from print_config when data loads
    React.useEffect(() => {
        const config = transactionData?.print_config as any;
        if (config?.sub_sections && Array.isArray(config.sub_sections) && config.sub_sections.length > 0) {
            const subKeys = config.sub_sections.map((obj: any) => Object.keys(obj)[0]).filter(Boolean);
            setFullLoadedConfig(config);
            setSubSections(subKeys);
            if (onSubSectionsLoaded) onSubSectionsLoaded(subKeys);
            if (!selectedSubSection || !subKeys.includes(selectedSubSection)) {
                setSelectedSubSection("");
            }
        } else {
            setFullLoadedConfig(null);
            setSubSections([]);
            if (onSubSectionsLoaded) onSubSectionsLoaded([]);
            setSelectedSubSection("");
        }
    }, [transactionData, onSubSectionsLoaded]);

    React.useEffect(() => {
        const baseConfig = transactionData?.print_config as any;
        const defaultLanguage = resolvedPrintConfig?.default_lang || baseConfig?.default_lang || "en";
        if (onLanguageSelected) {
            onLanguageSelected(defaultLanguage);
        }
    }, [resolvedPrintConfig, transactionData, onLanguageSelected]);

    const printPayload = transactionData?.print_payload;

    const chequeNumber =
        printPayload?.cheque_num
        || printPayload?.cheque_number
        || transactionData?.pl_transactions?.find((pt) => pt.cheque_num)?.cheque_num
        || transactionData?.gl_transactions?.find((gt) => gt.cheque_num)?.cheque_num
        || null;


    const filteredPlTransactions = transactionData?.pl_transactions?.filter((plTransaction) => {
        if (onlyCredits) {
            return plTransaction.credit! > 0;
        }
        if (onlyDebits) {
            return plTransaction.debit! > 0;
        }
        return true;
    }) || [];

    const plGlAccountIds = filteredPlTransactions
        .map(pt => pt.pl_account?.gl_account_id)
        .filter((id): id is number => !!id);

    const filteredGlTransactions = transactionData?.gl_transactions?.filter((glTransaction) => {
        if (onlyCredits) {
            return glTransaction.credit! > 0;
        }
        if (onlyDebits) {
            return glTransaction.debit! > 0;
        }
        return !plGlAccountIds.includes(glTransaction.gl_account_id);

    }) || [];

    const getRowAmount = (debit?: number | null, credit?: number | null) => {
        if (onlyDebits) return Number(debit || 0);
        if (onlyCredits) return Number(credit || 0);
        const creditValue = Number(credit || 0);
        const debitValue = Number(debit || 0);
        return creditValue !== 0 ? creditValue : debitValue;
    };

    const getMultilingualAccountName = (row: any) => {
        const cleanLang = activeLang.startsWith("si") ? "si" : activeLang.startsWith("ta") ? "ta" : "en";
        if (cleanLang === "si") {
            return row.account_name_ln2 || row.account_name_si || row.account_name_sinhala || row.account_name_ln1 || row.account_name || "N/A";
        }
        if (cleanLang === "ta") {
            return row.account_name_ln3 || row.account_name_ta || row.account_name_tamil || row.account_name_ln1 || row.account_name || "N/A";
        }
        return row.account_name_ln1 || row.account_name || "N/A";
    };

    const getFallbackPlAccountName = (plTransaction: any) => {
        const cleanLang = activeLang.startsWith("si") ? "si" : activeLang.startsWith("ta") ? "ta" : "en";
        if (cleanLang === "si") {
            return plTransaction.pl_account?.account_type?.name_ln2 || plTransaction.pl_account?.account_type?.name_ln1 || "N/A";
        }
        if (cleanLang === "ta") {
            return plTransaction.pl_account?.account_type?.name_ln3 || plTransaction.pl_account?.account_type?.name_ln1 || "N/A";
        }
        return plTransaction.pl_account?.account_type?.name_ln1 || "N/A";
    };

    const getFallbackGlAccountName = (glTransaction: any) => {
        const cleanLang = activeLang.startsWith("si") ? "si" : activeLang.startsWith("ta") ? "ta" : "en";
        if (cleanLang === "si") {
            return glTransaction.gl_account?.gl_coa?.name_ln2 || glTransaction.gl_account?.gl_coa?.name_ln1 || "N/A";
        }
        if (cleanLang === "ta") {
            return glTransaction.gl_account?.gl_coa?.name_ln3 || glTransaction.gl_account?.gl_coa?.name_ln1 || "N/A";
        }
        return glTransaction.gl_account?.gl_coa?.name_ln1 || "N/A";
    };

    const payloadRows: TransactionPrintRow[] = Array.isArray(printPayload?.accounts)
        ? (printPayload?.accounts || []).map((account, idx) => ({
            key: `payload-${idx}`,
            accountNumber: account.account_number || "N/A",
            accountName: getMultilingualAccountName(account),
            amount: Number(account.amount || 0),
            balance: Number(account.balance || 0),
        }))
        : [];

    const fallbackRows: TransactionPrintRow[] = [
        ...filteredPlTransactions.map((plTransaction) => ({
            key: `pl-${plTransaction.id}`,
            accountNumber: plTransaction.pl_account?.ref_account_number || "N/A",
            accountName: getFallbackPlAccountName(plTransaction),
            amount: getRowAmount(plTransaction.debit, plTransaction.credit),
            balance: Number(plTransaction.balance || 0),
        })),
        ...filteredGlTransactions.map((glTransaction) => ({
            key: `gl-${glTransaction.id}`,
            accountNumber: glTransaction.gl_account?.ref_number || "N/A",
            accountName: getFallbackGlAccountName(glTransaction),
            amount: getRowAmount(glTransaction.debit, glTransaction.credit),
            balance: Number(glTransaction.balance || 0),
        }))
    ];

    const rowsToRender = payloadRows.length > 0 ? payloadRows : fallbackRows;
    const totalAmount = rowsToRender.reduce((acc, row) => acc + Number(row.amount || 0), 0);

    React.useEffect(() => {
        // Only trigger printing on a rising edge of `doPrint` (false -> true),
        // and avoid re-printing the same transaction that was just printed.
        if (!transactionData?.print_payload) return;

        const didRise = doPrint && !prevDoPrintRef.current;
        const txnId = transactionData?.id ?? null;

        if (didRise) {
            if (isPrintingRef.current) {
                // already printing, skip
            } else if (txnId !== null && lastPrintedTransactionId.current === txnId) {
                // already printed this transaction recently, skip
            } else {
                handleDynamicPrint(transactionData.print_payload);
                lastPrintedTransactionId.current = txnId;
            }
        }

        prevDoPrintRef.current = doPrint;
    }, [doPrint, transactionData]);

    React.useEffect(() => {
        if (transactionData?.print_payload && onPrintReady) {
            onPrintReady(() => {
                return () => handleDynamicPrint(transactionData.print_payload!);
            });
        }
    }, [transactionData, activeLang, activeSubSection]);

    const normalizePrintConfig = (rawConfig: any) => {
        const source = rawConfig || {};

        return {
            ...defaultPrintConfig,
            ...source,
            page: {
                ...defaultPrintConfig.page,
                ...(source.page || {}),
            },
            layout: {
                ...defaultPrintConfig.layout,
                ...(source.layout || {}),
            },
            sections: {
                ...defaultPrintConfig.sections,
                ...(source.sections || {}),
                table: {
                    ...defaultPrintConfig.sections.table,
                    ...(source.sections?.table || {}),
                },
            },
            fields: {
                ...defaultPrintConfig.fields,
                ...(source.fields || {}),
            },
        };
    };

    const handleDynamicPrint = async (payload: TransactionPrintPayload) => {
        if (!payload) {
            toast.showError("No print payload available for this transaction.");
            return;
        }

        if (isPrintingRef.current) {
            return;
        }
        isPrintingRef.current = true;

        const total = Number(payload.total_amount ?? totalAmount ?? 0);

        // If receipt_print > 0 the transaction was already printed at least once.
        // Inject a reprint marker so printRenderer renders the REPRINT watermark
        // at the position configured in the print config (or the default top-right).
        const isReprint = Number((transactionData as any)?.receipt_print ?? 0) > 0;

        const config: any = normalizePrintConfig(resolvedPrintConfig);

        const primaryPlTx = transactionData?.pl_transactions?.[0];
        const primaryPlAccount = primaryPlTx?.pl_account;
        const rateVal = primaryPlAccount?.interest_rate != null ? primaryPlAccount.interest_rate : undefined;
        const periodVal = primaryPlAccount?.period != null ? primaryPlAccount.period : undefined;

        const irField = config?.fields?.interest_rate as any;
        const irpField = config?.fields?.interest_rate_period as any;
        const hidePercentIR = irField?.hide_percent === true || irField?.hide_percent === 1 || irField?.hide_percent === "true";
        const hidePercentIRP = irpField?.hide_percent === true || irpField?.hide_percent === 1 || irpField?.hide_percent === "true";

        const cleanLang = activeLang.startsWith("si") ? "si" : activeLang.startsWith("ta") ? "ta" : "en";
        const periodStr = periodVal != null ? (cleanLang === "si" ? `මාස ${periodVal}` : cleanLang === "ta" ? `மாதங்கள் ${periodVal}` : `${periodVal} Months`) : "";
        const rateStr = rateVal != null ? (hidePercentIR ? `${rateVal}` : `${rateVal}%`) : "";
        const rateStrIRP = rateVal != null ? (hidePercentIRP ? `${rateVal}` : `${rateVal}%`) : "";
        const interestRatePeriodStr = (periodVal != null && rateVal != null) ? 
            (cleanLang === "si" ? `මාස ${periodVal} කට පොලී අනුපාතය ${rateStrIRP}` : 
             cleanLang === "ta" ? `${periodVal} மாதங்களுக்கு வட்டி வீதம் ${rateStrIRP}` : 
             `Per ${periodVal} Months rate ${rateStrIRP}`) 
            : "";

        // Fetch guarantors for print if this is a loan account
        let guarantorMembersStr = "";
        const plAccountId = (primaryPlTx as any)?.pl_account_id || primaryPlAccount?.id;
        if (plAccountId) {
            try {
                const guarantors = await fetchGuarantorsForPrint(plAccountId);
                if (guarantors && guarantors.length > 0) {
                    const cleanLang = activeLang.startsWith("si") ? "si" : activeLang.startsWith("ta") ? "ta" : "en";
                    const GurLabel = cleanLang === "si" ? "ඇප" : cleanLang === "ta" ? "உத்தரவாதம்" : "Gur";
                    guarantorMembersStr = guarantors.map(g => `${GurLabel} ${g.index} - ${g.customer_number}`).join(", ");
                }
            } catch {
                // guarantors fetch failed — leave empty
            }
        }

        const enrichedPayload = {
            ...payload,
            interest_rate: payload.interest_rate || rateStr,
            period: payload.period || periodStr,
            interest_rate_period: payload.interest_rate_period || interestRatePeriodStr,
            description: payload.description || transactionData?.description || "",
            entered_by_id: payload.entered_by_id || (transactionData as any)?.entered_by_id || (transactionData as any)?.entered_by || "",
            cheque_number: payload.cheque_number || payload.cheque_num || chequeNumber || undefined,
            accounts: payload?.accounts || rowsToRender.map(r => ({
                account_number: r.accountNumber,
                account_name: r.accountName,
                amount: r.amount
            })),
            total_amount_words: numberToWords(total, activeLang),
            institute_reg_num: payload.institute_reg_num || transactionData?.institute?.reg_num || (transactionData?.branch as any)?.institute?.reg_num || "",
            institute_reg_date: payload.institute_reg_date || transactionData?.institute?.reg_date || (transactionData?.branch as any)?.institute?.reg_date || "",
            institute_address_ln1: payload.institute_address_ln1 || transactionData?.institute?.address_ln1 || (transactionData?.branch as any)?.institute?.address_ln1 || "",
            institute_address_ln2: payload.institute_address_ln2 || transactionData?.institute?.address_ln2 || (transactionData?.branch as any)?.institute?.address_ln2 || "",
            institute_address_ln3: payload.institute_address_ln3 || transactionData?.institute?.address_ln3 || (transactionData?.branch as any)?.institute?.address_ln3 || "",
            institute_phone: payload.institute_phone || transactionData?.institute?.phone || (transactionData?.branch as any)?.institute?.phone || "",
            institute_email: payload.institute_email || transactionData?.institute?.email || (transactionData?.branch as any)?.institute?.email || "",
            branch_phone: payload.branch_phone || (transactionData?.branch as any)?.telephone || "",
            branch_email: payload.branch_email || (transactionData?.branch as any)?.e_mail || "",
            guarantor_members: payload.guarantor_members || guarantorMembersStr,
            // Only set when reprinting — printRenderer skips the field when value is empty
            ...(isReprint ? { reprint: "** REPRINT **" } : {}),
        };

        const pageWidth = config.page.width || "21cm";
        const pageHeight = config.page.height || "29.7cm";

        setLoadingMessage("Generating print...");
        setLoading(true);

        try {
            const html = generatePrintHTML(enrichedPayload, config, activeLang);

            openPrintWindowHTML(html, pageWidth, pageHeight, "Print Receipt", async () => {
                if (transactionData?.id) {
                    try {
                        await updateReceiptPrintStatus(transactionData.id);
                        // Refresh transaction details to update the reprint count/status if needed
                        getTransaction(transactionData.id).then((data) => {
                            setTransactionData(data as GlTransactionHeaderWithPrintPayload);
                        }).catch(() => { });
                    } catch (error: any) {
                        toast.showError(`Failed to update receipt print status: ${error.message}`);
                    }
                }
            });
            // mark as printed for this transaction to avoid duplicate auto-prints
            lastPrintedTransactionId.current = transactionData?.id ?? null;
        } catch (error: any) {
            toast.showError(`Failed to generate print: ${error.message || error}`);
        } finally {
            setLoading(false);
            isPrintingRef.current = false;
        }
    };

    const hasConfig = !!(
        resolvedPrintConfig &&
        Object.keys(resolvedPrintConfig).length > 0
    );

    if (hasConfig && transactionData && printPayload) {
        const total = Number(printPayload.total_amount ?? totalAmount ?? 0);
        const isReprint = Number((transactionData as any)?.receipt_print ?? 0) > 0;
        const config = normalizePrintConfig(resolvedPrintConfig);

        const primaryPlTx = transactionData?.pl_transactions?.[0];
        const primaryPlAccount = primaryPlTx?.pl_account;
        const rateVal = primaryPlAccount?.interest_rate != null ? primaryPlAccount.interest_rate : undefined;
        const periodVal = primaryPlAccount?.period != null ? primaryPlAccount.period : undefined;

        const irField = config?.fields?.interest_rate as any;
        const irpField = config?.fields?.interest_rate_period as any;
        const hidePercentIR = irField?.hide_percent === true || irField?.hide_percent === 1 || irField?.hide_percent === "true";
        const hidePercentIRP = irpField?.hide_percent === true || irpField?.hide_percent === 1 || irpField?.hide_percent === "true";

        const cleanLang = activeLang.startsWith("si") ? "si" : activeLang.startsWith("ta") ? "ta" : "en";
        const periodStr = periodVal != null ? (cleanLang === "si" ? `මාස ${periodVal}` : cleanLang === "ta" ? `மாதங்கள் ${periodVal}` : `${periodVal} Months`) : "";
        const rateStr = rateVal != null ? (hidePercentIR ? `${rateVal}` : `${rateVal}%`) : "";
        const rateStrIRP = rateVal != null ? (hidePercentIRP ? `${rateVal}` : `${rateVal}%`) : "";
        const interestRatePeriodStr = (periodVal != null && rateVal != null) ? 
            (cleanLang === "si" ? `මාස ${periodVal} කට පොලී අනුපාතය ${rateStrIRP}` : 
             cleanLang === "ta" ? `${periodVal} மாதங்களுக்கு வட்டி வீதம் ${rateStrIRP}` : 
             `Per ${periodVal} Months rate ${rateStrIRP}`) 
            : "";

        const enrichedPayload = {
            ...printPayload,
            interest_rate: printPayload.interest_rate || rateStr,
            period: printPayload.period || periodStr,
            interest_rate_period: printPayload.interest_rate_period || interestRatePeriodStr,
            guarantor_members: printPayload.guarantor_members || guarantorMembersStr,
            description: printPayload.description || transactionData?.description || "",
            entered_by_id: printPayload.entered_by_id || (transactionData as any)?.entered_by_id || (transactionData as any)?.entered_by || "",
            cheque_number: printPayload.cheque_number || printPayload.cheque_num || chequeNumber || undefined,
            accounts: printPayload.accounts || rowsToRender.map(r => ({
                account_number: r.accountNumber,
                account_name: r.accountName,
                amount: r.amount
            })),
            total_amount_words: numberToWords(total, activeLang),
            institute_reg_num: printPayload.institute_reg_num || transactionData?.institute?.reg_num || (transactionData?.branch as any)?.institute?.reg_num || "",
            institute_reg_date: printPayload.institute_reg_date || transactionData?.institute?.reg_date || (transactionData?.branch as any)?.institute?.reg_date || "",
            institute_address_ln1: printPayload.institute_address_ln1 || transactionData?.institute?.address_ln1 || (transactionData?.branch as any)?.institute?.address_ln1 || "",
            institute_address_ln2: printPayload.institute_address_ln2 || transactionData?.institute?.address_ln2 || (transactionData?.branch as any)?.institute?.address_ln2 || "",
            institute_address_ln3: printPayload.institute_address_ln3 || transactionData?.institute?.address_ln3 || (transactionData?.branch as any)?.institute?.address_ln3 || "",
            institute_phone: printPayload.institute_phone || transactionData?.institute?.phone || (transactionData?.branch as any)?.institute?.phone || "",
            institute_email: printPayload.institute_email || transactionData?.institute?.email || (transactionData?.branch as any)?.institute?.email || "",
            branch_phone: printPayload.branch_phone || (transactionData?.branch as any)?.telephone || "",
            branch_email: printPayload.branch_email || (transactionData?.branch as any)?.e_mail || "",
            // Only set when reprinting — printRenderer skips the field when value is empty
            ...(isReprint ? { reprint: "** REPRINT **" } : {}),
        };

        const previewHTML = generatePrintHTML(enrichedPayload, config, activeLang);

        return (
            <>
                {loading && <Loading message={loadingMessage} />}
                {subSections.length > 0 && !onSubSectionChangeProp && (
                    <Box paddingBottom="small" display="flex" justifyContent="flexEnd">
                        <select
                            value={selectedSubSection}
                            onChange={(e) => handleSubSectionChange(e.currentTarget.value)}
                            style={{
                                padding: '4px 8px',
                                fontSize: '13px',
                                borderRadius: '4px',
                                border: '1px solid #c8ccd4',
                                backgroundColor: '#fff',
                                color: '#2b303a',
                                outline: 'none',
                                cursor: 'pointer',
                                height: '32px',
                                fontWeight: 500,
                            }}
                        >
                            <option value="">Default</option>
                            {subSections.map(s => (
                                <option key={s} value={s}>{s}</option>
                            ))}
                        </select>
                    </Box>
                )}
                <div
                    ref={containerRef}
                    style={{
                        width: "100%",
                        overflowX: "hidden",
                        overflowY: "auto",
                        backgroundColor: "#cdd1d980",
                        padding: "8px",
                        boxSizing: "border-box",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center"
                    }}
                >
                    <div
                        style={{
                            width: `${targetWidthVal * scale}px`,
                            height: scaledHeight ? `${scaledHeight}px` : "auto",
                            overflow: "hidden",
                            position: "relative"
                        }}
                    >
                        <div
                            ref={contentRef}
                            id="print-page"
                            style={{
                                width: `${targetWidthVal}px`,
                                boxShadow: "0 0 10px rgba(0,0,0,0.15)",
                                backgroundColor: "#fff",
                                transformOrigin: "top left",
                                transform: `scale(${scale})`,
                                position: "absolute",
                                left: 0,
                                top: 0
                            }}
                            dangerouslySetInnerHTML={{ __html: previewHTML }}
                        />
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            {loading && <Loading message={loadingMessage} />}
            <Box id="print-page">
                <Box className="print-header" padding={"xxsmall"} paddingTop={"small"} marginBottom={"small"} background="surface">
                    <Stack space="small">
                        <Heading level="3">{printPayload?.institute_name || transactionData?.institute?.name_ln1}</Heading>
                        <Heading level="4">{printPayload?.branch_name || transactionData?.branch?.name_ln1}</Heading>
                        <Text size="large">{printPayload?.transaction_type || transactionData?.it_transaction_type?.type_name}</Text>
                    </Stack>
                </Box>
                <Box className="print-content" padding={"xxsmall"} background="surface">
                    <Stack space="small">
                        <Columns space="large">
                            <Column width="1/2">
                                <Box bottom={0}>
                                    <Stack space="small">
                                        <Text tone="info">{cleanNA(printPayload?.customer_name || transactionData?.name)}</Text>
                                        <Text>{cleanNA(printPayload?.branch_address || transactionData?.address)}</Text>
                                        {transactionData?.description ? (<Text size="xsmall">{transactionData?.description}</Text>) : null}
                                    </Stack>
                                </Box>
                            </Column>
                            <Column width="1/2">
                                <Box alignItems={"flexEnd"} textAlign={"right"}>
                                    <Stack space="small">
                                        <Text tone="info">{cleanNA(printPayload?.transaction_number || transactionData?.transaction_number)} </Text>
                                        <Text weight="strong">{formatCurrency(Number(printPayload?.total_amount ?? transactionData?.amount))}</Text>
                                        <Text size="small">{new Date(transactionData?.enterd_date || '').toLocaleString()}</Text>
                                        {chequeNumber ? (<Text size="small">Cheque No: {chequeNumber}</Text>) : null}
                                    </Stack>
                                </Box>
                            </Column>
                        </Columns>
                        <Box className="account-details-table-container">
                            <Table label="Transaction Details">
                                <TableHeader>
                                    <TableRow>
                                        <TableHeaderCell><Text>Account NO</Text></TableHeaderCell>
                                        <TableHeaderCell width="30%"><Text>Account Name</Text></TableHeaderCell>
                                        {onlyCredits && onlyDebits ?
                                            <>
                                                <TableHeaderCell><Text>Debit</Text></TableHeaderCell>
                                                <TableHeaderCell><Text>Credit</Text></TableHeaderCell>
                                            </>
                                            :
                                            <TableHeaderCell align="right"><Text>Amount</Text></TableHeaderCell>
                                        }
                                        <TableHeaderCell align="right"><Text>Balance</Text></TableHeaderCell>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {rowsToRender.map((row) => (
                                        <TableRow key={row.key}>
                                            <TableCell><Text>{cleanNA(row.accountNumber)}</Text></TableCell>
                                            <TableCell><Text>{cleanNA(row.accountName)}</Text></TableCell>
                                            <TableCell align="right"><Text>{formatCurrency(Number(row.amount))}</Text></TableCell>
                                            <TableCell align="right"><Text>{formatCurrency(Number(row.balance))}</Text></TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                                <TableFooter>
                                    <TableRow>
                                        <TableCell colspan={2} align="right"><Text>Sub Total:</Text> </TableCell>
                                        <TableCell align="right"><Text>{formatCurrency(totalAmount)}</Text></TableCell>
                                        <TableCell align="right"><Text></Text></TableCell>
                                    </TableRow>
                                </TableFooter>
                            </Table>
                        </Box>
                    </Stack>
                </Box>
            </Box>
        </>
    );
}

export default TransactionPrintDetails;